/**
 * pluginComponentRegistry.ts - Plugin Component Registry
 * 
 * ARCHITECTURE:
 * - Creates lazy components on-demand (not all at startup)
 * - Components cached after first access
 * - No dynamic imports in render functions
 * - No useState for component caching
 * - Follows React best practices
 * 
 * PERFORMANCE:
 * - Only loads plugins when actually used
 * - Faster initial load time
 * - Reduced memory footprint
 * - Tree-shaking friendly
 * 
 * This solves the "Hooks order" problem by moving all lazy() calls
 * outside of component render logic.
 */

import React, { lazy, ComponentType } from 'react';
import { pluginRegistry } from './pluginRegistry';

/**
 * Registry of lazy-loaded plugin components
 * Populated on-demand as plugins are accessed
 */
const pluginComponentRegistry = new Map<string, ComponentType<any>>();

/**
 * Registry initialization flag
 */
let isInitialized = false;

/**
 * Initialize the component registry
 * This just sets up the registry structure, doesn't load components
 */
export function initializePluginComponents() {
  if (isInitialized) {
    console.log('[PluginComponentRegistry] Already initialized');
    return;
  }
  
  console.log('[PluginComponentRegistry] Initialized (lazy loading enabled)');
  isInitialized = true;
}

/**
 * Create a lazy component for a plugin
 * Called on-demand when plugin is first accessed
 */
function createPluginComponent(pluginId: string): ComponentType<any> | null {
  const plugin = pluginRegistry.find(p => p.id === pluginId);
  
  if (!plugin) {
    console.warn(`[PluginComponentRegistry] Plugin not found: ${pluginId}`);
    return null;
  }
  
  if (!plugin.enabled) {
    console.warn(`[PluginComponentRegistry] Plugin disabled: ${pluginId}`);
    return null;
  }
  
  try {
    console.log(`[PluginComponentRegistry] Creating lazy component for: ${pluginId}`);
    
    // Handle Pluggr-sourced plugins
    if (plugin.source === 'pluggr') {
      console.log(`[PluginComponentRegistry] Loading from Pluggr via API: ${plugin.path}`);
      
      // Load component metadata from API
      const LazyComponent = lazy(async () => {
        try {
          // Get component info from API
          const response = await fetch(`http://api.opcentral.localhost/api/plugins/${pluginId}/component`);
          const data = await response.json();
          
          if (!data.success) {
            throw new Error(data.error || 'Failed to load plugin component');
          }
          
          const { package: pkg, export: exportName } = data.component;
          
          console.log(`[PluginComponentRegistry] Loading ${exportName} from ${pkg}`);
          
          // Import from Dependr (orchestrator pattern)
          const module = await import('@opcentral/dependr');
          const component = (module as any)[exportName];
          
          if (!component) {
            throw new Error(`Component ${exportName} not found in @opcentral/dependr`);
          }
          
          return { default: component };
        } catch (error) {
          console.error(`[PluginComponentRegistry] Error loading plugin ${pluginId}:`, error);
          throw error;
        }
      });
      
      return LazyComponent;
    }
    
    // Create lazy component for local plugins
    const LazyComponent = lazy(() => 
      import(/* @vite-ignore */ plugin.path).then(module => {
            // Try multiple export patterns for COMPONENTS (not Plugin objects)
            const component = 
              module.DataTransformer ||           // Data Transformer component
              module.TagManagerPluginComponent || // Tag Manager component (renamed export)
              module.PickerSelectr ||             // Picker component
              module.PackageTest ||       // Named component exports
              module.TableViewer ||
              module.FieldMatcher ||
              module.WorkflowManagerPlugin ||     // Workflow Manager component
              module.WorkflowRunnerPlugin ||      // Workflow Runner component (legacy)
              module.WorkflowInspectorPlugin ||   // Workflow Inspector component (legacy)
              module.ScriptConverterPlugin ||     // Script Converter component
              module[plugin.name] ||      // Named export matching plugin name
              // Find any React component (function or class with $$typeof)
              Object.values(module).find((exp: any) => {
                // Skip the Plugin object (has manifest/onload/onunload)
                if (exp && typeof exp === 'object' && (exp.manifest || exp.onload)) {
                  return false;
                }
                // Look for React components
                return typeof exp === 'function' || 
                       (exp && typeof exp === 'object' && exp.$$typeof);
              }) ||
              module.default;             // Default export as last resort
            
            if (!component) {
              console.warn(`[PluginComponentRegistry] No component found for ${plugin.id}`);
              // Return a fallback component
              return {
                default: () => React.createElement('div', 
                  { style: { padding: '2rem', textAlign: 'center' } },
                  React.createElement('h2', null, '⚠️ Component Not Found'),
                  React.createElement('p', null, 'Plugin: ', React.createElement('strong', null, plugin.name)),
                  React.createElement('p', null, 'Path: ', React.createElement('code', null, plugin.path))
                )
              };
            }
            
            return { default: component };
          })
        );
        
    return LazyComponent;
  } catch (error) {
    console.error(`[PluginComponentRegistry] Failed to create component for ${pluginId}:`, error);
    return null;
  }
}

/**
 * Get a plugin component by ID
 * Creates the component on first access (lazy initialization)
 */
export function getPluginComponent(pluginId: string): ComponentType<any> | null {
  // Check if already in registry
  if (pluginComponentRegistry.has(pluginId)) {
    return pluginComponentRegistry.get(pluginId)!;
  }
  
  // Create component on-demand
  const component = createPluginComponent(pluginId);
  
  if (component) {
    // Cache for future use
    pluginComponentRegistry.set(pluginId, component);
  }
  
  return component;
}

/**
 * Check if a plugin has a registered component
 */
export function hasPluginComponent(pluginId: string): boolean {
  return pluginComponentRegistry.has(pluginId);
}

/**
 * Preload specific plugins (optional optimization)
 * Call this to eagerly load critical plugins
 */
export function preloadPlugins(pluginIds: string[]) {
  console.log(`[PluginComponentRegistry] Preloading ${pluginIds.length} plugins...`);
  pluginIds.forEach(id => getPluginComponent(id));
}
